package fr.frux.feature.notifications

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.NotificationsNone
import androidx.compose.material.icons.outlined.Settings
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import fr.frux.app.resources.Res
import fr.frux.app.resources.notifications_empty_message
import fr.frux.app.resources.notifications_empty_title
import fr.frux.app.resources.notifications_load_more
import fr.frux.app.resources.notifications_mark_all_read
import fr.frux.app.resources.notifications_preferences_action
import fr.frux.app.resources.notifications_title
import fr.frux.core.designsystem.components.EmptyState
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxCard
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.shared.domain.model.NotificationSubjectType
import org.jetbrains.compose.resources.stringResource

/**
 * Écran Activité (#225 P0) : liste paginée des notifications, non-lues distinguées visuellement,
 * clic → marque lu + navigue vers le sujet.
 *
 * @param onOpenSubject `subjectType`/`subjectId` de l'item cliqué — l'appelant (entryProvider)
 *   traduit vers `Route.HarvestDetail`/`Route.TreeDetail`.
 */
@Composable
fun NotificationsScreen(
    onBack: () -> Unit,
    onOpenPreferences: () -> Unit,
    onOpenSubject: (NotificationSubjectType, Long) -> Unit,
    viewModel: NotificationsViewModel = rememberNotificationsViewModel(),
) {
    val state by viewModel.state.collectAsState()

    // Même raisonnement que MyTreesScreen : le VM survit au push/pop mais la composition de cet
    // écran est démontée puis remontée par Nav3 — on recharge à chaque retour dessus.
    LaunchedEffect(viewModel) {
        viewModel.load()
    }

    Scaffold(
        topBar = {
            FruxTopBar(
                title = stringResource(Res.string.notifications_title),
                onBack = onBack,
                actions = {
                    FruxIconButton(
                        icon = Icons.Outlined.Settings,
                        contentDescription = stringResource(Res.string.notifications_preferences_action),
                        onClick = onOpenPreferences,
                    )
                },
            )
        },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        when {
            state.isLoading -> {
                LoadingState(modifier = Modifier.padding(paddingValues))
            }

            state.error != null -> {
                ErrorState(
                    message = state.error!!.userMessage(),
                    onRetry = viewModel::load,
                    modifier = Modifier.padding(paddingValues),
                )
            }

            state.items.isEmpty() -> {
                EmptyState(
                    icon = Icons.Outlined.NotificationsNone,
                    title = stringResource(Res.string.notifications_empty_title),
                    message = stringResource(Res.string.notifications_empty_message),
                    modifier = Modifier.padding(paddingValues),
                )
            }

            else -> {
                LazyColumn(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentPadding = PaddingValues(FruxSpacing.md),
                    verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                ) {
                    item {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.End,
                        ) {
                            TextButton(onClick = viewModel::markAllRead) {
                                Text(stringResource(Res.string.notifications_mark_all_read))
                            }
                        }
                    }

                    items(state.items, key = { it.id }) { item ->
                        NotificationRow(
                            item = item,
                            onMarkRead = { viewModel.markRead(item.id) },
                            onOpen = { onOpenSubject(item.subjectType, item.subjectId) },
                        )
                    }

                    if (state.hasMore) {
                        item {
                            Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                                if (state.isLoadingMore) {
                                    CircularProgressIndicator(modifier = Modifier.size(24.dp))
                                } else {
                                    TextButton(onClick = viewModel::loadMore) {
                                        Text(stringResource(Res.string.notifications_load_more))
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NotificationRow(
    item: NotificationItem,
    onMarkRead: () -> Unit,
    onOpen: () -> Unit,
) {
    // #338 : une ligne a soit une destination, soit aucune affordance de clic. Sans tap pour la
    // marquer lue, une ligne sans destination se marque lue à l'affichage.
    val hasDestination = item.hasDestination
    if (!hasDestination && item.isUnread) {
        LaunchedEffect(item.id) { onMarkRead() }
    }
    val onClick: (() -> Unit)? =
        if (hasDestination) {
            {
                onMarkRead()
                onOpen()
            }
        } else {
            null
        }
    FruxCard(modifier = Modifier.fillMaxWidth(), onClick = onClick) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (item.isUnread) {
                Box(
                    modifier =
                        Modifier
                            .size(8.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primary),
                )
                Spacer(modifier = Modifier.size(FruxSpacing.sm))
            }
            Text(
                text = item.title,
                style = if (item.isUnread) MaterialTheme.typography.bodyLarge else MaterialTheme.typography.bodyMedium,
                color =
                    if (item.isUnread) {
                        MaterialTheme.colorScheme.onSurface
                    } else {
                        MaterialTheme.colorScheme.onSurfaceVariant
                    },
                modifier = Modifier.weight(1f),
            )
        }
    }
}
